import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, ChevronRight, RotateCcw } from "lucide-react";
import {
  SPECIES,
  CHALLENGES,
  getProductsBySpecies,
  getProductsBySpeciesAndChallenge,
} from "@/data/products";
import { buildWaLink, WA_TEMPLATES } from "@/lib/whatsapp";

/**
 * Compact 2-step Product Finder used on the homepage.
 *
 * Reuses the exact same matching helpers as ProductFinder — protected logic.
 * Purpose: give a Home-visitor a fast preview (Species → Health Need) and then
 * hand off to the Products page (or Vet Professionals for the full finder).
 */
export default function CompactProductFinder() {
  const [species, setSpecies] = useState(null);
  const [challenge, setChallenge] = useState(null);

  const results = useMemo(() => {
    if (!species) return [];
    if (!challenge) return getProductsBySpecies(species);
    const refined = getProductsBySpeciesAndChallenge(species, challenge);
    return refined.length ? refined : getProductsBySpecies(species);
  }, [species, challenge]);

  const reset = () => {
    setSpecies(null);
    setChallenge(null);
  };

  const speciesLabel = species && SPECIES.find((s) => s.id === species)?.label;
  const challengeLabel =
    challenge && CHALLENGES.find((c) => c.id === challenge)?.label;

  return (
    <div
      className="rounded-2xl bg-white border border-bah-line p-6 md:p-8 shadow-sm"
      data-testid="compact-product-finder"
    >
      {/* Step 1 — Species */}
      {!species && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-bah-green">
            Step 1 · Species
          </div>
          <h3 className="mt-2 font-display text-2xl md:text-3xl text-bah-ink">
            What animal are you caring for?
          </h3>
          <div className="mt-5 flex flex-wrap gap-2">
            {SPECIES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSpecies(s.id)}
                data-testid={`compact-finder-species-${s.id}`}
                className="px-4 py-2 rounded-full border border-bah-line bg-bah-paper text-sm font-semibold text-bah-ink hover:border-bah-green hover:text-bah-green transition-colors"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2 — Health need */}
      {species && !challenge && (
        <div>
          <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-bah-green">
            <span>Species:</span>
            <span className="px-2 py-0.5 rounded-full bg-bah-green/10">
              {speciesLabel}
            </span>
          </div>
          <h3 className="mt-3 font-display text-2xl md:text-3xl text-bah-ink">
            Step 2 · Health need
          </h3>
          <div className="mt-5 flex flex-wrap gap-2">
            {CHALLENGES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setChallenge(c.id)}
                data-testid={`compact-finder-challenge-${c.id}`}
                className="px-4 py-2 rounded-full border border-bah-line bg-bah-paper text-sm font-semibold text-bah-ink hover:border-bah-green hover:text-bah-green transition-colors"
              >
                {c.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={reset}
            data-testid="compact-finder-reset-step2"
            className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-bah-mute hover:text-bah-ink"
          >
            <RotateCcw className="h-3 w-3" /> Start over
          </button>
        </div>
      )}

      {/* Results */}
      {species && challenge && (
        <div data-testid="compact-finder-results">
          <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-bah-green">
            <span className="px-2 py-0.5 rounded-full bg-bah-green/10">
              {speciesLabel}
            </span>
            <ChevronRight className="h-3.5 w-3.5 text-bah-mute" />
            <span className="px-2 py-0.5 rounded-full bg-bah-orange/10 text-bah-orange">
              {challengeLabel}
            </span>
          </div>
          <h3 className="mt-3 font-display text-2xl md:text-3xl text-bah-ink">
            {results.length > 0
              ? `${Math.min(results.length, 3)} suggestion${
                  Math.min(results.length, 3) === 1 ? "" : "s"
                }`
              : "No exact match"}
          </h3>

          {results.length > 0 ? (
            <ul className="mt-5 space-y-2">
              {results.slice(0, 3).map((p) => (
                <li
                  key={p.id}
                  className="flex items-start justify-between gap-3 rounded-xl border border-bah-line bg-bah-paper px-4 py-3"
                  data-testid={`compact-finder-result-${p.id}`}
                >
                  <div>
                    <div className="font-display text-base text-bah-ink leading-tight">
                      {p.name}
                    </div>
                    <div className="text-xs text-bah-mute mt-0.5 uppercase tracking-wider">
                      {p.category.replace(/-/g, " ")}
                    </div>
                  </div>
                  <Link
                    to={`/products?q=${encodeURIComponent(p.name)}`}
                    className="shrink-0 text-xs font-semibold text-bah-green hover:text-bah-orange inline-flex items-center gap-1 whitespace-nowrap"
                  >
                    View <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-bah-mute">
              Talk to our technical team on WhatsApp — we'll help you find the
              right Biodeal product.
            </p>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={`/products?species=${species}${
                challenge ? `&need=${challenge}` : ""
              }`}
              data-testid="compact-finder-see-all"
              className="btn-primary text-sm"
            >
              See all matches <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={buildWaLink(
                `${WA_TEMPLATES.technical_general} Species: ${speciesLabel}. Need: ${challengeLabel}.`
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp text-sm"
              data-testid="compact-finder-wa"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp technical team
            </a>
            <button
              type="button"
              onClick={reset}
              data-testid="compact-finder-reset"
              className="btn-ghost-dark text-sm"
            >
              <RotateCcw className="h-4 w-4" /> Start over
            </button>
          </div>
          <p className="mt-5 text-[11px] text-bah-mute leading-relaxed">
            Guidance tool — not a substitute for veterinary judgement. For the
            full 3-step finder with responsible-use notes, visit{" "}
            <Link to="/vet-professionals" className="underline text-bah-green">
              Vet Professionals
            </Link>
            .
          </p>
        </div>
      )}
    </div>
  );
}
